import { useState } from 'react'

import { beginEnrolment, confirmEnrolment, login, verifyCode } from '../api/auth'
import { ApiError } from '../api/client'
import type { Session } from '../api/types'

type Stage = 'credentials' | 'code' | 'enrol'

/**
 * Signing in, including the second factor.
 *
 * The three stages are one screen rather than three routes: a person halfway through
 * signing in has no useful URL to bookmark, and a back button that drops them into a
 * half-authenticated state is a bug waiting to be filed.
 */
export function LoginPage({
  session,
  onSignedIn,
}: {
  session: Session | undefined
  onSignedIn: () => void
}) {
  const initial: Stage = session?.mfa.enrolled ? 'code' : session ? 'enrol' : 'credentials'
  const [stage, setStage] = useState<Stage>(initial)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [secret, setSecret] = useState('')
  const [uri, setUri] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function attempt(work: () => Promise<Session | void>) {
    setBusy(true)
    setError(null)
    try {
      const result = await work()
      if (result && 'mfa' in result) {
        if (result.mfa.verified) return onSignedIn()
        if (result.mfa_enrolment_required) {
          const started = await beginEnrolment()
          setSecret(started.secret)
          setUri(started.otpauth_uri)
          return setStage('enrol')
        }
        return setStage('code')
      }
      onSignedIn()
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'sign-in failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main
      style={{ display: 'grid', placeItems: 'center', height: '100%' }}
      aria-labelledby="signin"
    >
      <form
        className="panel"
        style={{ width: 360 }}
        onSubmit={(event) => {
          event.preventDefault()
          if (stage === 'credentials') return attempt(() => login({ username, password }))
          if (stage === 'code') return attempt(() => verifyCode(code))
          return attempt(() => confirmEnrolment(code))
        }}
      >
        <h1 id="signin" style={{ marginTop: 0 }}>
          Sentry
        </h1>

        {error && (
          <p className="error" role="alert" style={{ marginBottom: 'var(--space-3)' }}>
            {error}
          </p>
        )}

        {stage === 'credentials' && (
          <>
            <label htmlFor="username">Username</label>
            <input
              id="username"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              style={{ width: '100%', marginBottom: 'var(--space-3)' }}
            />
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              style={{ width: '100%', marginBottom: 'var(--space-4)' }}
            />
          </>
        )}

        {stage === 'enrol' && (
          <>
            <p className="muted">
              This account can change what the platform does, so it needs a second factor.
              Add this secret to your authenticator, then enter the code it shows.
            </p>
            <p className="mono" style={{ wordBreak: 'break-all' }}>
              {secret}
            </p>
            {uri && (
              <p className="muted mono" style={{ wordBreak: 'break-all', fontSize: 11 }}>
                {uri}
              </p>
            )}
          </>
        )}

        {(stage === 'code' || stage === 'enrol') && (
          <>
            <label htmlFor="code">Authenticator code</label>
            <input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              style={{ width: '100%', marginBottom: 'var(--space-4)' }}
            />
          </>
        )}

        <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Working…' : stage === 'credentials' ? 'Sign in' : 'Continue'}
        </button>
      </form>
    </main>
  )
}
